import './ProfileLinks.css';

export const LINK_TYPES = [
  { id: 'website', label: 'Website' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'strava', label: 'Strava' },
  { id: 'x', label: 'X / Twitter' },
  { id: 'other', label: 'Other link' },
];
const LABELS = Object.fromEntries(LINK_TYPES.map(t => [t.id, t.label]));

export function normalizeUrl(u) {
  const s = String(u || '').trim();
  if (!s) return '';
  return /^https?:\/\//i.test(s) ? s : `https://${s}`;
}

function LinkIcon({ type }) {
  const p = { width: 15, height: 15, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (type) {
    case 'youtube': return <svg {...p}><path d="M22.5 6.4a2.8 2.8 0 0 0-2-2C18.8 4 12 4 12 4s-6.8 0-8.5.4a2.8 2.8 0 0 0-2 2A29 29 0 0 0 1.2 12a29 29 0 0 0 .3 5.6 2.8 2.8 0 0 0 2 2C5.2 20 12 20 12 20s6.8 0 8.5-.4a2.8 2.8 0 0 0 2-2 29 29 0 0 0 .3-5.6 29 29 0 0 0-.3-5.6Z" /><path d="m10 15 5-3-5-3z" fill="currentColor" /></svg>;
    case 'instagram': return <svg {...p}><rect x="2" y="2" width="20" height="20" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" /></svg>;
    case 'facebook': return <svg {...p}><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" /></svg>;
    case 'tiktok': return <svg {...p}><path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" /></svg>;
    case 'strava': return <svg {...p}><path d="M9 4 4 14h3l2-4 2 4h3z" /><path d="m13 14 2 4 2-4h2l-4 6-4-6z" fill="currentColor" stroke="none" /></svg>;
    case 'x': return <svg {...p}><path d="M4 4l16 16M20 4 4 20" /></svg>;
    case 'website': return <svg {...p}><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15 15 0 0 1 0 20 15 15 0 0 1 0-20z" /></svg>;
    default: return <svg {...p}><path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" /><path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" /></svg>;
  }
}

/** Renders a creator's business badge + contact + social link icons. Null if empty. */
export function ProfileLinks({ profile, hideBusiness = false }) {
  const links = (profile?.links || []).filter(l => l?.url);
  const hasBiz = !hideBusiness && profile?.isBusiness && profile?.businessName;
  const email = profile?.isBusiness ? profile?.businessEmail : '';
  const phone = profile?.isBusiness ? profile?.businessPhone : '';
  if (!links.length && !hasBiz && !email && !phone) return null;
  return (
    <div className="profile-links">
      {hasBiz && (
        <span className="profile-links__biz" title={profile.businessVerifiedStatus === 'verified' ? 'Verified business (ABN + insurance checked)' : undefined}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-3" /></svg>
          {profile.businessName}
          {profile.businessVerifiedStatus === 'verified' && (
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#16a34a' }}><polyline points="20 6 9 17 4 12" /></svg>
          )}
        </span>
      )}
      {email && (
        <a href={`mailto:${email}`} className="profile-links__link" title={email}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m2 7 10 6 10-6" /></svg>
        </a>
      )}
      {phone && (
        <a href={`tel:${phone}`} className="profile-links__link" title={phone}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3-8.6A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.4-1.2a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z" /></svg>
        </a>
      )}
      {links.map((l, i) => (
        <a key={i} href={normalizeUrl(l.url)} target="_blank" rel="noopener noreferrer" className="profile-links__link" title={LABELS[l.type] || l.url}>
          <LinkIcon type={l.type} />
        </a>
      ))}
    </div>
  );
}
